import React, { useContext } from "react";
import TotalYearCard from "../../../components/totalrevenue/totalyearcard/TotalYearCard";
import TotalQuarterCard from "../../../components/totalrevenue/totalquarterard/TotalQuarterCard";
import TotalMonthCard from "../../../components/totalrevenue/totalmonthcard/TotalMonthCard";
import SubHeader from "../../../components/subheader/SubHeader";
import "./OverAll.scss";
import RevenueLineChart from "../../../components/revenue/revenuechart/RevenueLineChart";
import RevenueTable from "../../../components/revenue/revenuetable/RevenueTable";
import USRevenueMap from "../../../components/revenue/revenuebystatemap/RevenueByStateMap";
import RevenuePieChart from "../../../components/revenue/revenuepiechart/RevenuePieChart";
import RevenueTableCategories from "../../../components/revenue/revenuetablecategories/RevenueTableCategories";
import RevenueBarChart from "../../../components/revenue/revenuebarchart/RevenueBarChart";
import RegionalRevenueDonutChart from "../../../components/revenue/donutchart/DonutChart";
import { AppContext } from "../../../context/Context";
import { getQuarterByMonth, createRevenueDateTitle, createRevenueCardYearTitle, createRevenueCardQuarterTitle, createRevenueCardMonthTitle } from "./../../../utils/format";

const OverAll = () => {
  const { data, fromDate, toDate } = useContext(AppContext);

  // Current Revenue Card
  const lineData = data?.revenue?.overall?.line || [];

  const firstRevenueYearMonth =
    lineData.length > 0 ? lineData[0].year_month : null;

  const lastRevenueYearMonth =
    lineData.length > 0 ? lineData[lineData.length - 1].year_month : null;
  
  const computePreviousMonthAmount = (data) => {
    if (!data.revenue?.overall.previousMonth) return 0;
    return data.revenue.overall.previousMonth.reduce(
      (sum, item) => sum + item.revenue_income,
      0
    );
  }

  // Revenue Card - Year
  data.revenue?.overall.totalrevenue
    .filter((item) => {
      if (fromDate === "All") return true;

      // Compare full YYYY-MM strings
      return item.year_month >= fromDate && item.year_month <= toDate;
    })
    .reduce((acc, curr) => acc + curr.revenue_income, 0);

  const computeRevenueCardYearAmount = (fromDate, toDate, data) => {
    let total = 0;

    if (fromDate === "All" || !toDate) {
      total =
        data.revenue?.overall.totalrevenue?.reduce(
          (sum, item) => sum + (item.revenue_income || 0),
          0
        ) || 0;
    } else {
      const fromYear = parseInt(fromDate.split("-")[0]);
      const toYear = parseInt(toDate.split("-")[0]);

      total =
        data.revenue?.overall.totalrevenue
          ?.filter((item) => {
            const itemYear = parseInt(item.year_month.split("-")[0]);
            return itemYear >= fromYear && itemYear <= toYear;
          })
          .reduce((sum, item) => sum + (item.revenue_income || 0), 0) || 0;
    }

    // Format with commas and 2 decimal places
    return total;
  };

  const computeRevenueCardQuarterAmount = (fromDate, toDate, data) => {
    if (!data?.revenue?.overall.totalrevenue) return 0;

    // Handle "All"
    if (fromDate === "All" || !toDate) {
      return data.revenue.overall.totalrevenue.reduce(
        (sum, item) => sum + (item.revenue_income || 0),
        0
      );
    }

    // --- Parse from/to year & month ---
    const [fromYear, fromMonth] = fromDate.split("-").map(Number);
    const [toYear, toMonth] = toDate.split("-").map(Number);

    const fromQuarter = getQuarterByMonth(fromMonth);
    const toQuarter = getQuarterByMonth(toMonth);

    // --- Filter ---
    const total = data.revenue.overall.totalrevenue
      .filter((item) => {
        const [itemYear, itemMonth] = item.year_month.split("-").map(Number);
        const itemQuarter = getQuarterByMonth(itemMonth);

        // year before range
        if (itemYear < fromYear || itemYear > toYear) return false;

        // same year as start
        if (itemYear === fromYear && itemQuarter < fromQuarter) return false;

        // same year as end
        if (itemYear === toYear && itemQuarter > toQuarter) return false;

        // in between years → always included
        return true;
      })
      .reduce((sum, item) => sum + (item.revenue_income || 0), 0);

    return total;
  };

  const computeRevenueCardMonthAmount = (data) => {
    const lines = data?.revenue?.overall?.line;
    if (!Array.isArray(lines)) return "0.00";

    const total = lines.reduce(
      (sum, item) => sum + (item.revenue_income || 0),
      0
    );

    return total;
  };

  // -------------- Custom Data region ---------------------------------------------------
  const regionColors = {
    "Region 2": "#CCFF33",
    "Region 3": "#70E000",
    "Region 1": "#52B69A",
    "(None)": "#9ca3af",
  };
  const sumByRegion = (data) => {
    if (!Array.isArray(data)) {
      console.error("Invalid data: Expected an array but got", data);
      return {};
    }
    return data.reduce((acc, item) => {
      acc[item.regional] = (acc[item.regional] || 0) + item.revenue_income;
      return acc;
    }, {});
  };

  const formattedData = Object.entries(
    sumByRegion(data.revenue?.overall.region)
  )
    .map(([region, totalRevenue]) => ({
      region,
      totalRevenue,
      indicator: regionColors[region] || "#000000",
    }))
    .sort((a, b) => b.totalRevenue - a.totalRevenue);

  const formattedDataRemoveNone = Object.entries(
    sumByRegion(data.revenue?.overall.region)
  )
    .map(([region, totalRevenue]) => ({
      name: region,
      value: totalRevenue,
    }))
    .filter((item) => item.name !== "(None)")
    .sort((a, b) => b.value - a.value);

  // -------------------------- Format Revenue Line Chart --------------------------------------------

  const renameKeys = (data) => {
    if (!Array.isArray(data)) {
      console.error("renameKeys: data is not an array or is undefined", data);
      return [];
    }
    return data.map((item) => ({
      month: item.year_month,
      value: item.revenue_income,
    }));
  };

  //------------------- Highest State Data ----------------------------------------------------

  const sumByState = (data) => {
    if (!Array.isArray(data)) {
      console.error("Invalid data: Expected an array but got", data);
      return {};
    }
    return data.reduce((acc, item) => {
      acc[item.state] = (acc[item.state] || 0) + item.revenue_income;
      return acc;
    }, {});
  };

  const sumState = sumByState(data.revenue?.overall.revenuestateincome);

  const revenueStateArray = Object.entries(sumState)
    .map(([state, revenue]) => ({
      state,
      revenue: parseFloat(revenue.toFixed(2)),
    }))
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, 5);

  // -------------------------------------- hisghest Market ----------------------------------------------

  const sumByMarket = (data) => {
    if (!Array.isArray(data)) {
      console.error("Invalid data: Expected an array but got", data);
      return {};
    }
    return data.reduce((acc, item) => {
      acc[item.market] = (acc[item.market] || 0) + item.revenue_income;
      return acc;
    }, {});
  };

  const sumMarket = sumByMarket(data.revenue?.overall.revenuemarket);

  const revenueMarketArray = Object.entries(sumMarket)
    .map(([state, revenue]) => ({
      state,
      revenue: parseFloat(revenue.toFixed(2)),
    }))
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, 5);

  // ----------------------------------- revenue plan ------------------------------------

  const sumByPlan = (data) => {
    if (!Array.isArray(data)) {
      console.error("Invalid data: Expected an array but got", data);
      return {};
    }
    return data.reduce((acc, item) => {
      acc[item.plan] = (acc[item.plan] || 0) + item.revenue_income;
      return acc;
    }, {});
  };

  const sumPlan = sumByPlan(data.revenue?.overall.revenueplan);

  const revenuePlanArray = Object.entries(sumPlan)
    .map(([name, value]) => ({
      name,
      value: parseFloat(value.toFixed(2)),
    }))
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, 5);

  // ------------------------------- data revenue categories ------------------------------------

  // Step 1: Find the latest and previous months dynamically
  const uniqueMonths = [
    ...new Set(
      data.revenue?.overall.revenuecategories.map((item) => item.year_month)
    ),
  ].sort();
  const previousMonths = uniqueMonths[uniqueMonths.length - 2];

  // Step 2: Create a map of last month's revenue income
  const lastMonthData = {};
  data.revenue?.overall.revenuecategories.forEach((item) => {
    if (item.year_month === previousMonths) {
      lastMonthData[item.account_dashboard_rename] = item.revenue_income;
    }
  });

  const transformedData = data.revenue?.overall.revenuecategories
    // .filter((item) => item.year_month === latestMonth)
    .map((item) => {
      const lastPeriodValue = data.revenue.overall.revenuecategories
      .filter(
        (x) =>
          x.account_dashboard_rename === item.account_dashboard_rename &&
          x.range_type === "previous"
      )
      .reduce((sum, x) => sum + (x.revenue_income || 0), 0);
      const currentValue = data.revenue.overall.revenuecategories
      .filter(
        (x) =>
          x.account_dashboard_rename === item.account_dashboard_rename &&
          x.range_type === "current"
      )
      .reduce((sum, x) => sum + (x.revenue_income || 0), 0);
      
      // Safe percent change
      let percentChange = 0;
      if (lastPeriodValue === 0) {
        percentChange = currentValue > 0 ? 100 : 0;
      } else {
        percentChange = ((currentValue - lastPeriodValue) / lastPeriodValue) * 100;
      }

      return {
        account_dashboard_rename: item.account_dashboard_rename,
        revenue_income: currentValue,
        revenue_income_lastmonth: lastPeriodValue,
        percentChange,
      };
    })
    // prevent duplicates (optional)
    .filter(
      (v, i, a) =>
        a.findIndex(
          (t) => t.account_dashboard_rename === v.account_dashboard_rename
        ) === i
    )
    .sort((a, b) => b.revenue_income - a.revenue_income);

  return (
    <div className="frame-main-overall">
      <SubHeader title="TOTAL REVENUE & INCOME" />

      <div className="frame-block1-overall">
        <div className="frame-block1-overall-left">
          <div className="frame-block1-overall-left-container">
            <div className="frame-block1-overall-left-top">
              <div className="frame-block1-overall-left-top-left">
                <TotalYearCard
                  title={createRevenueCardYearTitle(firstRevenueYearMonth, lastRevenueYearMonth, fromDate, toDate)}
                  amount={computeRevenueCardYearAmount(fromDate, toDate, data)}
                />
              
              </div>
              <div className="frame-block2-overall-left-top-left">
                <TotalQuarterCard
                  title={createRevenueCardQuarterTitle(firstRevenueYearMonth, lastRevenueYearMonth, fromDate, toDate)}
                  amount={computeRevenueCardQuarterAmount(fromDate, toDate, data)}
                />
                
              </div>

              <div className="frame-block3-overall-left-top-left">
                <TotalMonthCard
                  title={createRevenueCardMonthTitle(firstRevenueYearMonth, lastRevenueYearMonth, fromDate, toDate)}
                  amount={computeRevenueCardMonthAmount(data)}
                  previousMonthAmount={computePreviousMonthAmount(data)}
                />
                
              </div>
            </div>
            <div className="frame-block1-overall-left-bottom">
              <RevenueLineChart
                color="#82ca9d"
                data={renameKeys(data.revenue?.overall.totalrevenue)}
              />
            </div>
          </div>
        </div>
        <div className="frame-block1-overall-right">
          <div className="frame-block1-overall-right-top">
            <h3>TOTAL INCOME BY REGIONAL</h3>
            <div className="frame-donut-chart-main">
              <RegionalRevenueDonutChart
                colors={["#CCFF33", "#70E000", "#52B69A"]}
                data={formattedDataRemoveNone}
              />
            </div>
          </div>
          <div className="frame-block1-overall-right-bottom">
            <RevenueTable data={formattedData} />
          </div>
        </div>
      </div>
      <div className="frame-block2-overall">
        <div className="frame-block2-overall-left">
          <h3>Total Income by State</h3>
          <RevenueBarChart
            height={400}
            firstColor="#EAFFBD"
            secondColor="#52B69A"
            data={revenueStateArray}
          />
        </div>
        <div className="frame-block2-overall-right">
          <h3>Total Income by State</h3>
          <USRevenueMap
            revenueData={sumState}
            colors={[
              "#e5f5e0",
              "#c7e9c0",
              "#a1d99b",
              "#74c476",
              "#41ab5d",
              "#238b45",
              "#006d2c",
            ]}
          />
        </div>
      </div>
      <div className="frame-block3-overall">
        <div className="frame-block3-overall-left">
          <h3>Total Income by Market</h3>
          <RevenueBarChart
            height={400}
            firstColor="#EAFFBD"
            secondColor="#52B69A"
            data={revenueMarketArray}
          />
        </div>
        <div className="frame-block3-overall-right">
          <h3>Total Income by Plan</h3>
          <div className="frame-revenue-piechart">
            <RevenuePieChart
              colors={["#5DAF91", "#C8F76F", "#77DA29"]}
              data={revenuePlanArray}
            />
          </div>
        </div>
      </div>
      <div className="frame-block4-overall">
        <h3>TOTAL REVENUE & INCOME BY CATEGORIES ({createRevenueDateTitle(firstRevenueYearMonth, lastRevenueYearMonth, fromDate, toDate)})</h3>
        <RevenueTableCategories
          data={transformedData}
          from_date={fromDate}
          headerTitle={[
            "Categories",
            "Total Revenue",
            "Total Revenue (Last Period)",
            "% Change",
          ]}
        />
      </div>
    </div>
  );
};

export default OverAll;
